import { useEffect, useMemo, useState, type ReactNode } from 'react';

/*
 * Class-based syntax highlighting (highlight.js grammars via lowlight). The
 * colors live in course.css, so nothing here produces inline styles. The
 * grammars load on first use; code shows as plain text until then.
 */

type Highlighter = (code: string, language: string) => ReactNode;

let highlighter: Highlighter | null = null;
let loading: Promise<Highlighter> | null = null;

function loadHighlighter(): Promise<Highlighter> {
  return (loading ??= import('./highlighter').then((m) => {
    highlighter = m.highlightCode;
    return highlighter;
  }));
}

const ALIASES: Record<string, string> = {
  py: 'python',
  ipython: 'python',
  ipython3: 'python',
  python3: 'python',
  js: 'javascript',
  mjs: 'javascript',
  cjs: 'javascript',
  jsx: 'javascript',
  ts: 'typescript',
  tsx: 'typescript',
  rs: 'rust',
  rb: 'ruby',
  sh: 'bash',
  zsh: 'bash',
  shell: 'bash',
  yml: 'yaml',
  cs: 'csharp',
  'c#': 'csharp',
  h: 'c',
  hpp: 'cpp',
  'c++': 'cpp',
  md: 'markdown',
  html: 'xml',
  htm: 'xml',
  svg: 'xml',
  toml: 'ini',
  kt: 'kotlin',
  golang: 'go',
  text: 'plaintext',
  txt: 'plaintext',
};

/** Normalized language name for a fence label or file extension. */
export function normalizeLanguage(name: string | null | undefined): string {
  if (!name) return 'plaintext';
  const key = name.toLowerCase().trim();
  return ALIASES[key] ?? key;
}

/** Highlighted React nodes for some code (plain text until the grammars load). */
export function useHighlighted(code: string, language: string): ReactNode {
  const lang = normalizeLanguage(language);
  const plain = lang === 'plaintext';
  const [ready, setReady] = useState<Highlighter | null>(highlighter);
  useEffect(() => {
    if (plain || ready) return;
    let cancelled = false;
    loadHighlighter()
      .then((h) => !cancelled && setReady(() => h))
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [plain, ready]);
  return useMemo(() => (ready && !plain ? ready(code, lang) : code), [ready, plain, code, lang]);
}

const LABELS: Record<string, string> = {
  javascript: 'JavaScript',
  typescript: 'TypeScript',
  python: 'Python',
  csharp: 'C#',
  cpp: 'C++',
  xml: 'HTML/XML',
  bash: 'Bash',
  json: 'JSON',
  yaml: 'YAML',
  sql: 'SQL',
  css: 'CSS',
  php: 'PHP',
  ini: 'TOML/INI',
  go: 'Go',
  plaintext: 'Text',
};

/** Display name for a language label. */
export function languageLabel(name: string | null | undefined): string {
  if (!name) return '';
  const lang = normalizeLanguage(name);
  return LABELS[lang] ?? lang.charAt(0).toUpperCase() + lang.slice(1);
}
